import Layout from '@/components/feature/Layout';
import SEOHead from '@/components/base/SEOHead';
import { localBusinessSchema, buildBreadcrumbSchema, buildFaqSchema, buildServiceSchema, buildGraphSchema } from '@/components/base/schemas';
import { Link } from 'react-router-dom';
import { useState } from 'react';

const serviceSchema = buildServiceSchema(
  'Réparation de baies vitrées à Paris 15ᵉ',
  'Baie vitrée qui bloque, déraille ou ferme mal dans le 15ᵉ ? Réparation galets, rails, serrure.'
);

const breadcrumbSchema = buildBreadcrumbSchema([
  { name: 'Accueil', item: 'https://fenetre-paris15.fr/' },
  { name: 'Baies vitrées', item: 'https://fenetre-paris15.fr/reparation-baie-vitree-paris-15/' },
]);

const faqs = [
  { q: `Ma baie vitrée coulissante est très dure à ouvrir. Que faire ?`, a: `Ne forcez pas, vous risqueriez de sortir le vantail de son rail ou de casser les galets. Le problème vient généralement de galets usés ou encrassés, ou d'un rail déformé. DF PRO nettoie et remplace les galets si nécessaire, puis règle le galetage pour une glisse fluide.` },
  { q: `La baie vitrée ne ferme plus complètement. Est-ce dangereux ?`, a: `Oui, une baie qui ne ferme plus correctement est un risque de sécurité et d'isolation. Le problème peut venir du mécanisme de verrouillage, d'un galet sorti du rail ou d'un rail obstrué. Nous diagnostiquons la cause et réparons rapidement.` },
  { q: `Intervenez-vous sur les grandes baies vitrées du Front de Seine ?`, a: `Oui, c'est une de nos spécialités. Les baies vitrées de grande dimension, typiques du Front de Seine et de Beaugrenelle, nécessitent des galets renforcés et un réglage précis. Nous avons l'expérience et les pièces adaptées.` },
];

export default function BaiePage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <Layout>
      <SEOHead
        title="Réparation Baie Vitrée Paris 15 (75015) — Coulissant | DF PRO"
        description="Baie vitrée qui bloque, déraille ou ferme mal dans le 15ᵉ ? Réparation galets, rails, serrure. Devis gratuit 24/48h. ☎ 01 87 66 81 55"
        canonicalUrl="https://fenetre-paris15.fr/reparation-baie-vitree-paris-15/"
        schema={buildGraphSchema(localBusinessSchema, breadcrumbSchema, buildFaqSchema(faqs), serviceSchema)}
      />

      <div className="bg-brand-mint/30 py-3">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <nav aria-label="Fil d'Ariane" className="text-xs text-brand-text/60">
            <Link to="/" className="hover:text-brand-accent">Accueil</Link><span className="mx-2">/</span><span className="text-brand-accent font-medium">Baies vitrées</span>
          </nav>
        </div>
      </div>

      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
            <div>
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-brand-dark leading-tight mb-4">Réparation de baies vitrées à Paris 15ᵉ</h1>
              <p className="text-brand-text/70 text-base leading-relaxed mb-6">
                Votre baie vitrée coulissante <strong>bloque, déraille ou ferme mal</strong> ? DF PRO est le spécialiste des baies vitrées dans le 15ᵉ. Galets, rails, serrures, joints : nous réparons tous les mécanismes de vos baies coulissantes, y compris les grandes dimensions du Front de Seine.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/devis-fenetre-paris-15/" className="btn-primary text-sm">Devis gratuit</Link>
                <a href="tel:+33187668155" className="btn-outline text-sm"><i className="ri-phone-line"></i> 01 87 66 81 55</a>
              </div>
            </div>
            <div className="aspect-[4/3] rounded-2xl overflow-hidden">
              <img src="/images/dfpro-baie-hero.webp" alt="Grande baie vitrée coulissante — DF PRO Paris 15" className="w-full h-full object-cover" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-20 bg-brand-mint/40">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h2 className="section-title mb-8">Problèmes courants de baies vitrées coulissantes</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {[
              { title: `Baie dure à glisser`, desc: `Vous devez forcer pour ouvrir ou fermer. Les galets de roulement sont encrassés, usés ou déréglés. Un nettoyage du rail et un remplacement des galets redonnent une glisse fluide.` },
              { title: `Baie qui déraille`, desc: `Le vantail sort de son rail inférieur. Danger immédiat : la baie peut basculer. Souvent causé par des galets cassés ou un rail déformé. Intervention urgente.` },
              { title: `Galets usés ou cassés`, desc: `Avec le poids et le temps, les galets s'usent, se déforment ou se cassent. Le vantail frotte sur le rail et s'abîme. Remplacement des galets et réglage du galetage.` },
              { title: `Rail abîmé ou encrassé`, desc: `Poussière, gravillons, déformation du rail. Un rail sale use les galets prématurément. Nettoyage en profondeur ou remplacement du rail si nécessaire.` },
              { title: `Serrure ou crémone HS`, desc: `Le mécanisme de verrouillage ne fonctionne plus, la baie ne ferme pas à clé. Remplacement du barillet, de la crémone ou du mécanisme complet.` },
              { title: `Joint défectueux ou infiltration`, desc: `Courant d'air, eau qui s'infiltre en partie basse. Le joint brosse ou le joint central est usé. Remplacement pour restaurer l'étanchéité.` },
            ].map((item, idx) => (
              <div key={idx} className="bg-white rounded-xl p-5 md:p-6">
                <h3 className="font-heading font-bold text-brand-dark text-base mb-2">{item.title}</h3>
                <p className="text-brand-text/60 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
            <div className="aspect-[4/3] rounded-2xl overflow-hidden">
              <img src="/images/dfpro-baie-repair.webp" alt="Réparation galets baie vitrée — DF PRO Paris 15" className="w-full h-full object-cover" loading="lazy" />
            </div>
            <div>
              <h2 className="section-title mb-4">Spécialiste des grandes baies du Front de Seine</h2>
              <p className="text-brand-text/70 leading-relaxed mb-4">
                Le quartier du Front de Seine et Beaugrenelle concentre des immeubles de grande hauteur équipés de <strong>baies vitrées coulissantes de grandes dimensions</strong>. Ces baies panoramiques, souvent de 2 à 3 mètres de large, imposent des contraintes mécaniques importantes sur les galets et les rails. Le poids du vitrage (parfois plus de 100 kg par vantail) accélère l'usure des pièces mobiles.
              </p>
              <p className="text-brand-text/70 leading-relaxed mb-6">
                DF PRO connaît ces configurations spécifiques et dispose de <strong>galets renforcés</strong> adaptés aux charges lourdes. Nous intervenons régulièrement dans les résidences du Front de Seine, de Beaugrenelle et des immeubles contemporains du 15ᵉ.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/devis-fenetre-paris-15/" className="btn-primary text-sm">Devis gratuit</Link>
                <Link to="/quartier-beaugrenelle/" className="btn-outline text-sm">Quartier Beaugrenelle</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-20 bg-brand-mint/40">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <h2 className="section-title mb-8 text-center">Questions fréquentes — Baies vitrées</h2>
          <div className="space-y-3">
            {faqs.map((faq, idx) => (
              <div key={idx} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <button onClick={() => setOpenFaq(openFaq === idx ? null : idx)} className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-brand-mint/30 transition-colors">
                  <span className="font-heading font-bold text-brand-dark text-sm pr-4">{faq.q}</span>
                  <i className={`ri-${openFaq === idx ? 'subtract' : 'add'}-line text-brand-accent text-lg flex-shrink-0`}></i>
                </button>
                {openFaq === idx && <div className="px-5 pb-4"><p className="text-brand-text/70 text-sm">{faq.a}</p></div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-white text-center">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <h2 className="section-title mb-4">Votre baie vitrée coulisse mal ?</h2>
          <p className="section-subtitle mx-auto mb-6">DF PRO répare vos baies vitrées dans tout Paris 15ᵉ — devis gratuit</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-8">
            <Link to="/devis-fenetre-paris-15/" className="btn-primary text-sm">Devis gratuit</Link>
            <a href="tel:+33187668155" className="btn-outline text-sm"><i className="ri-phone-line"></i> 01 87 66 81 55</a>
          </div>
          <div className="flex flex-wrap justify-center gap-4 text-sm">
            <Link to="/zones-intervention-paris-15/" className="text-brand-accent hover:underline">Zones d'intervention</Link>
            <span className="text-brand-text/30">|</span>
            <Link to="/quartier-beaugrenelle/" className="text-brand-accent hover:underline">Quartier Beaugrenelle</Link>
            <span className="text-brand-text/30">|</span>
            <Link to="/blog/double-vitrage-embue-que-faire/" className="text-brand-accent hover:underline">Article : double vitrage embué</Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}